// SeoPageEditor.jsx
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

/** Props:
 *  - apiKey (string)     X-API-Key
 *  - pageId (number?)    seo_pages.id to edit; omit to create new
 */
export default function SeoPageEditor({ apiKey, pageId }) {
  const baseURL = "https://gantasoft.in/rankridgeschool/dev/v1";
  const api = axios.create({ baseURL, headers: { "X-API-Key": "12345678" } });

  const fileRef = useRef(null);

  const [pages, setPages] = useState([]);
  const [subpages, setSubpages] = useState([]);

  const [form, setForm] = useState({
    page_id: "",
    subpage_id: "",
    slug: "",
    title: "",
    description: "",
    canonical_path: "",
    og_image: ""
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // load pages initially
  useEffect(() => {
    api.get("/pages?status=active")
      .then(({data}) => setPages(data.items || []))
      .catch(e => setErr(e?.response?.data?.message || e.message));
  }, []);

  // if editing, load existing seo page
  useEffect(() => {
    if (!pageId) return;
    setLoading(true);
    api.get(`/seo_pages/${pageId}`)
      .then(({data}) => {
        const p = data.item || {};
        setForm(f => ({
          ...f,
          page_id: p.page_id || "",
          subpage_id: p.subpage_id || "",
          slug: p.slug || "",
          title: p.title || "",
          description: p.description || "",
          canonical_path: p.canonical_path || "",
          og_image: p.og_image || ""
        }));
        // load subpages for selected page
        if (p.page_id) {
          api.get(`/subpages?status=active&page_id=${p.page_id}`)
            .then(({data}) => setSubpages(data.items || []))
            .catch(() => {});
        }
      })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoading(false));
  }, [pageId]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  // when page changes, fetch subpages and reset subpage_id
  async function onPageChange(e) {
    const pid = e.target.value;
    setForm(f => ({ ...f, page_id: pid, subpage_id: "" }));
    setSubpages([]);
    if (pid) {
      try {
        const {data} = await api.get(`/subpages?status=active&page_id=${pid}`);
        setSubpages(data.items || []);
      } catch {}
    }
  }

  function pickFile() { fileRef.current?.click(); }
  function onFileSelect(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setPreview(URL.createObjectURL(f));
    setProgress(0); setMsg(""); setErr("");
  }

  async function uploadOgImage() {
    const f = fileRef.current?.files?.[0];
    if (!f) return setErr("Choose a file first.");
    try {
      setErr(""); setMsg(""); setProgress(0);
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post(`/upload`, fd, {
        onUploadProgress: (evt) => { if (evt.total) setProgress(Math.round(100 * evt.loaded / evt.total)); }
      });
      if (data?.success && data?.path) {
        setForm(s => ({ ...s, og_image: data.path }));
        setMsg(`Uploaded: ${data.path}`);
      } else { throw new Error(data?.message || "Upload failed"); }
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
  }

  async function savePage() {
    if (!form.slug || !form.title || !form.description || !form.canonical_path) {
      return setErr("Please fill slug, title, description, canonical_path.");
    }
    setSaving(true); setErr(""); setMsg("");
    const payload = {
      page_id: form.page_id ? Number(form.page_id) : null,
      subpage_id: form.subpage_id ? Number(form.subpage_id) : null,
      slug: form.slug,
      title: form.title,
      description: form.description,
      canonical_path: form.canonical_path,
      og_image: form.og_image || null
    };
    try {
      if (pageId) {
        await api.put(`/seo_pages/${pageId}`, payload);
        setMsg("Page updated ✅");
      } else {
        const { data } = await api.post(`/seo_pages`, payload, { headers: { "Content-Type": "application/json" } });
        setMsg(`Page created ✅ (id: ${data?.id || "—"})`);
      }
    } catch (e) { setErr(e?.response?.data?.message || e.message || "Save failed"); }
    finally { setSaving(false); }
  }

  return (
    <div style={card}>
      <h3 style={h3}>SEO Page Editor</h3>

      {loading ? <p>Loading…</p> : (
        <>
          <div style={row}>
            <label style={label}>Page</label>
            <select name="page_id" value={form.page_id} onChange={onPageChange} style={input}>
              <option value="">— Select Page —</option>
              {pages.map(p => <option key={p.page_id} value={p.page_id}>{p.page_name}</option>)}
            </select>
          </div>

          <div style={row}>
            <label style={label}>Subpage</label>
            <select name="subpage_id" value={form.subpage_id} onChange={onChange} style={input} disabled={!form.page_id}>
              <option value="">— Optional —</option>
              {subpages.map(s => <option key={s.subpage_id} value={s.subpage_id}>{s.subpage_name}</option>)}
            </select>
          </div>

          <div style={row}>
            <label style={label}>Slug</label>
            <input name="slug" value={form.slug} onChange={onChange} placeholder="/academics/primary" style={input} />
          </div>

          <div style={row}>
            <label style={label}>Title</label>
            <input name="title" value={form.title} onChange={onChange} placeholder="Primary Academics | Rankridge" style={input} />
          </div>

          <div style={row}>
            <label style={label}>Description</label>
            <textarea name="description" value={form.description} onChange={onChange} placeholder="Explore our primary programs…" style={textarea} />
          </div>

          <div style={row}>
            <label style={label}>Canonical Path</label>
            <input name="canonical_path" value={form.canonical_path} onChange={onChange} placeholder="/academics/primary" style={input} />
          </div>

          <div style={row}>
            <label style={label}>OG Image</label>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <input readOnly value={form.og_image} placeholder="/public_uploads/abc123.jpg" style={{ ...input, flex: 1 }} />
                <button type="button" onClick={pickFile} style={btn("black","white")}>Choose</button>
                <button type="button" onClick={uploadOgImage} style={btn("#059669","white")}>Upload</button>
              </div>
              <input ref={fileRef} type="file" accept="image/*,.svg,.ico" onChange={onFileSelect} style={{ display:"none" }}/>
              {progress>0 && progress<100 && (
                <div style={{ height:8, background:"#e5e7eb", borderRadius:6, marginTop:8 }}>
                  <div style={{ width:`${progress}%`, height:8, background:"#059669", borderRadius:6 }} />
                </div>
              )}
              {preview && <img src={preview} alt="preview" style={{ maxHeight:150, marginTop:10, borderRadius:8, border:"1px solid #e5e7eb" }} />}
            </div>
          </div>

          <button type="button" onClick={savePage} disabled={saving} style={btn("#1d4ed8","white")}>
            {pageId ? "Update Page" : "Create Page"}
          </button>

          {msg && <p style={{ color:"#065f46", fontSize:13, marginTop:10 }}>{msg}</p>}
          {err && <p style={{ color:"#dc2626", fontSize:13, marginTop:6 }}>{err}</p>}
        </>
      )}
    </div>
  );
}

const card = { maxWidth: 780, border:"1px solid #e5e7eb", borderRadius:12, padding:16 };
const h3 = { marginBottom:12, fontWeight:700 };
const row = { display:"flex", gap:12, alignItems:"flex-start", marginBottom:12 };
const label = { width:160, paddingTop:8, fontSize:13, color:"#374151" };
const input = { flex:1, padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:10 };
const textarea = { ...input, minHeight:90 };
function btn(bg, color){ return { padding:"10px 14px", background:bg, color, border:"none", borderRadius:10, cursor:"pointer" }; }
